import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { regulationStartUpdate, selectRegulation } from "../../app/actions/regulationActions";
import { debugLogPages } from "../../app/helpers/debugHelper";
import { resolveFlowRoleContext } from "../../app/helpers/flowEngineConfigHelper";
import { attachCoordinatorPublishedEditNote, useFlowEditAfterAction } from "../../app/helpers/flowEditPageHelper";
import useCanSaveFlowEditRecord from "../../app/hooks/useCanSaveFlowEditRecord";
import { useHydrateModuleEditRecord } from "../../app/helpers/moduleEditHydrateHelper";
import { resolveFichaReturnPath } from "../../app/helpers/moduleFichaNavHelper";
import useFormValidations from "../../app/hooks/useFormValidations";
import useModuleNewEditFields from "../../app/hooks/useModuleNewEditFields";
import { attachModuleFormScope } from "../../app/helpers/moduleFormScopeHelper";
import NavbarIconsModuleContext from "../generic-nav-bar/NavbarIconsModuleContext";
import { getSelectedProblemsOriginalCatalogsHelper, hydrateCatalogSelectFields, listToStringHelper } from "../../app/helpers/helperCatalogs";
import { concatDynamicLevelsHelper } from "../../app/helpers/helperConcat";
import useHydrateMultilevelSelects from "../../app/hooks/useHydrateMultilevelSelects";
import FlowFichaSections from "../specialist-flow-engine/components/FlowFichaSections";
import FlowEngineSection from "../specialist-flow-engine/FlowEngineSection";
import FlowEngineBandejaBanner from "../specialist-flow-engine/FlowEngineBandejaBanner";
import RecordExcelUpdateHeaderButton from "../specialist-flow-engine/components/RecordExcelUpdateHeaderButton";
import FlowEditPageTitle from "../specialist-flow-engine/components/FlowEditPageTitle";
import useFlowEditPageTitle from "../../app/hooks/useFlowEditPageTitle";

const EditRegulation = () => {
  debugLogPages(':: EditRegulation');

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  /********Store*******/
  const { regulationSelected } = useSelector((state) => state.regulation);

  const { listFullCatalogCiius, listCatalogCiiusLevel1, listCatalogCiiusLevel2, listCatalogCiiusLevel3, listCatalogCiiusLevel4, listCatalogCiiusLevel5, listCatalogCiiusLevel6 } =
    useSelector((state) => state.catalogCiiu);

  const { listFullCatalogSections, listCatalogSectionsLevel1, listCatalogSectionsLevel2, listCatalogSectionsLevel3 } =
    useSelector((state) => state.catalogSection);

  const { listFullCatalogPhases, listCatalogPhasesLevel1, listCatalogPhasesLevel2, listCatalogPhasesLevel3, listCatalogPhasesLevel4 } =
    useSelector((state) => state.catalogPhase);

  const { listContinents, listRegions, listCountries, objectCountrySelectedInitial, objectRegionSelectedInitial, objectContinentSelectedInitial } = useSelector((state) => state.locationCountry);

  const { validationFields, arrayListPrincipal, arrayListSecondary, visibilityFlag } = useModuleNewEditFields("regulation", {
    record: regulationSelected,
    process: "regulation"
  });

  const userRole = useSelector((state) => state.userRole);
  const auth = useSelector((state) => state.auth);
  const flowEngine = useSelector((state) => state.flowEngine);

  const { listSeries, listSophistications, listMagnitudes, listSizes, listTypologies, listGeneralAdaptations, listStructuralAdaptations, listSpecificAdaptations, listProblems, listMultimedias, listPriceRanges,
    listPermissionPublish } = useSelector((state) => state.catalog);

  /********UseForm*******/
  const { values, setValues, errors, handleChange, handleChangeMulti, handleSubmit,
    handleChangeCatalogCiiu, handleChangeCatalogSection, handleChangeCatalogPhase, handleChangeLocation, handleChangeLocationRender } = useFormValidations(
      () => RegulationCallback(),
      regulationSelected,
      validationFields
    );

  useHydrateModuleEditRecord({
    moduleProcess: "regulation",
    selectedRecord: regulationSelected,
    setValues,
    selectAction: selectRegulation,
    catalogs: {
      listProblems,
      listGeneralAdaptations,
      listStructuralAdaptations,
      listSpecificAdaptations
    }
  });

  /********useState y useEffect*******/
  const [isDisabledProcessButton] = useState(false);

  useHydrateMultilevelSelects({
    values,
    setValues,
    listFullCatalogCiius,
    listFullCatalogSections,
    listFullCatalogPhases
  });

  useEffect(() => {
  // Guard clause: Ensures the component doesn't break if dependencies are missing or uninitialized
  if (typeof handleChange !== 'function' || !values) return;

  // Exact catalog data structure mapping for this specific module
  const catalogMappings = [
    { id: values.catalogserie_id, list: listSeries, field: "catalogserie_id" },
    { id: values.catalogproblem_id, list: listProblems, field: "catalogproblem_id" },
    { id: values.catalogpermissionpublish_id, list: listPermissionPublish, field: "catalogpermissionpublish_id" }
  ];

  // Securely iterate and match each key with its target catalog entry
  hydrateCatalogSelectFields(handleChange, catalogMappings);

// Optimized dependency framework targeting specific primitive fields instead of the whole object
}, [
  listSeries,
  listProblems,
  listPermissionPublish,
  values?.catalogserie_id,
  values?.catalogproblem_id,
  values?.catalogpermissionpublish_id
]);


  useEffect(() => {
    // 1. Guard de Ejecución: Verifica que la función externa exista antes de continuar
    if (typeof handleChangeLocationRender !== 'function') return;

    // 2. Definición estructurada de las locaciones iniciales
    const locations = [
      { object: objectContinentSelectedInitial, field: "catalogcontinents_id" },
      { object: objectRegionSelectedInitial, field: "catalogregions_id" },
      { object: objectCountrySelectedInitial, field: "catalogcountries_id" }
    ];

    // 3. Procesamiento directo sin bucles indexados artificiales
    locations.forEach(({ object, field }) => {
      // Guard de Propiedades: Valida existencia, tipos correctos y IDs válidos mayores a cero
      if (
        object &&
        object.id !== undefined &&
        object.id !== "undefined" && // Protección por si el backend envía el string "undefined"
        Number(object.id) > 0
      ) {
        handleChangeLocationRender(object, field);
      }
    });

    // 4. Dependencias limpias y específicas
  }, [objectContinentSelectedInitial, objectRegionSelectedInitial, objectCountrySelectedInitial]);

  const RegulationCallback = async () => {
    debugLogPages(':: EditRegulation, RegulationCallback, values', {values}); 

    let valuesSubmit = { ...values };

    valuesSubmit.general_adaptation = valuesSubmit.general_adaptation_string_list;
    valuesSubmit.structural_adaptation = valuesSubmit.structural_adaptation_string_list;
    valuesSubmit.specific_adaptation = valuesSubmit.specific_adaptation_string_list;

    // 1. Extrae las descripciones usando la propiedad por defecto ('description')
    valuesSubmit.catalogproblem_o = listToStringHelper(valuesSubmit.catalogproblem_list);

    // 2. Extrae los IDs pasando 'id' como tercer parámetro
    valuesSubmit.catalogproblem_ids = listToStringHelper(valuesSubmit.catalogproblem_list, ";", "id");

    valuesSubmit.catalogciiu_levels = concatDynamicLevelsHelper(valuesSubmit, "catalogciiu", 6);

    valuesSubmit.deleted = false;

    debugLogPages(':: EditRegulation, RegulationCallback, valuesSubmit', {valuesSubmit});
    const noteGate = await attachCoordinatorPublishedEditNote(valuesSubmit, {
      record: values,
      roleKey: roleContext.roleCode,
      flowEngine,
      t
    });
    if (!noteGate.proceed) {
      return;
    }
    dispatch(regulationStartUpdate(attachModuleFormScope(noteGate.valuesSubmit, visibilityFlag), navigate));

  };


  const getMethodData = (currentData) => {
  const dataMapping = {
    series: { comboData: listSeries, level: 1 },
    ciius_level1: { comboData: listCatalogCiiusLevel1, level: 1 },
    ciius_level2: { comboData: listCatalogCiiusLevel2, level: 2 },
    ciius_level3: { comboData: listCatalogCiiusLevel3, level: 3 },
    ciius_level4: { comboData: listCatalogCiiusLevel4, level: 4 },
    ciius_level5: { comboData: listCatalogCiiusLevel5, level: 5 },
    ciius_level6: { comboData: listCatalogCiiusLevel6, level: 6 },
    sections_level1: { comboData: listCatalogSectionsLevel1, level: 1 },
    sections_level2: { comboData: listCatalogSectionsLevel2, level: 2 },
    sections_level3: { comboData: listCatalogSectionsLevel3, level: 3 },
    phases_level1: { comboData: listCatalogPhasesLevel1, level: 1 },
    phases_level2: { comboData: listCatalogPhasesLevel2, level: 2 },
    phases_level3: { comboData: listCatalogPhasesLevel3, level: 3 },
    phases_level4: { comboData: listCatalogPhasesLevel4, level: 4 },
    phases_level5: { comboData: listCatalogPhasesLevel4, level: 5 }, // Corregido para apuntar a su catálogo correspondiente
    sophistications: { comboData: listSophistications, level: 1 },
    magnitudes: { comboData: listMagnitudes, level: 1 },
    sizes: { comboData: listSizes, level: 1 },
    typologies: { comboData: listTypologies, level: 1 },
    problems: { comboData: listProblems, level: 1 },
    multimedias: { comboData: listMultimedias, level: 1 },
    continents: { comboData: listContinents, level: 1 },
    regions: { comboData: listRegions, level: 1 },
    countries: { comboData: listCountries, level: 1 },
    prices: { comboData: listPriceRanges, level: 1 },
      permission_publish: { comboData: listPermissionPublish, level: 1 },
    general_adaptation: { comboData: listGeneralAdaptations, level: 1 },
    structural_adaptation: { comboData: listStructuralAdaptations, level: 1 },
    specific_adaptation: { comboData: listSpecificAdaptations, level: 1 }
  };

  const result = dataMapping[currentData] || { comboData: [], level: 0 };

  return {
    comboData: result.comboData,
    level: result.level
  };
};



  debugLogPages(':: EditRegulation', { 'values': values });
  debugLogPages(':: EditRegulation', { 'errors': errors });

  // 1. Configuración de claves relacionales del módulo de Normativas / Regulación
  const regulationRelations = {
    problems: "ModProblem_To_Regulation",
    multimedia: "ModuleFile_To_Regulation"
  };

  // 2. Propiedades unificadas para inyectar en el motor genérico
  const sharedFormProps = {
    values,
    errors,
    setValues,
    handleChange,
    handleChangeLocation,
    handleChangeMulti,
    handleChangeCatalogPhase,
    handleChangeCatalogCiiu,
    handleChangeCatalogSection,
    getMethodData,
    listProblems,
    listMultimedias,
    getSelectedProblemsOriginalCatalogs: getSelectedProblemsOriginalCatalogsHelper, // Helper con sufijo "Helper"
    relationKeys: regulationRelations,
    isEditMode: true // IMPORTANTÍSIMO: Habilita adaptaciones temporales en el switch interno
  };

  const handleFlowAfterAction = useFlowEditAfterAction({
    moduleProcess: "regulation",
    recordId: values?.id,
    dispatch,
    setValues,
    selectAction: selectRegulation
  });

  const roleContext = useMemo(
    () => resolveFlowRoleContext({ userRole, authUser: auth?.user }),
    [userRole, auth]
  );

  const canSaveRecord = useCanSaveFlowEditRecord(values, "regulation");
  const isSaveDisabled = isDisabledProcessButton || !canSaveRecord;

  const pageTitle = useFlowEditPageTitle("regulation");

  return (
    <div>
      <NavbarIconsModuleContext moduleProcess="regulation" />
      <FlowEditPageTitle title={pageTitle}>
        <RecordExcelUpdateHeaderButton
          moduleProcess="regulation"
          expectedName={values?.name}
          expectedId={values?.id}
          disabled={isSaveDisabled}
          onApplied={() => navigate(resolveFichaReturnPath("regulation"))}
        />
      </FlowEditPageTitle>
      <hr className="line-hr" />
      <FlowEngineBandejaBanner record={values} moduleProcess="regulation" />
      <FlowEngineSection
        record={values}
        moduleProcess="regulation"
        moduleName={pageTitle}
        layoutMode="edit"
        compact
        showHistoryButton={false}
        onAfterAction={handleFlowAfterAction}
      />
      <div className="ajusteTamanio">
        <form onSubmit={handleSubmit} noValidate>
          <fieldset disabled={isSaveDisabled} className="border-0 p-0 m-0">
          <div className="row">
            <FlowFichaSections
              record={values}
              process="regulation"
              arrayListPrincipal={arrayListPrincipal}
              arrayListSecondary={arrayListSecondary}
              sharedFormProps={sharedFormProps}
              visibilityFlag={visibilityFlag}
            />
          </div>

          {!isSaveDisabled && (
          <div className="btnGuardar">
            <input type="submit" value={t("buttons.submit")} className="btn btn-primary" />
          </div>
          )}
          </fieldset>
          <FlowEngineSection
            record={values}
            moduleProcess="regulation"
            moduleName={pageTitle}
            layoutMode="edit"
            showSummary
            showActions={false}
            onAfterAction={handleFlowAfterAction}
          />
        </form>
      </div>
    </div>
  );
};

export default EditRegulation;